import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import * as ls from 'local-storage';

import { generateOtp } from 'apis/rest/Authentication';
import loginValidationCriteria from 'components/app/utils/validators/loginValidator';
import useAlert from 'components/app/utils/hooks/useAlert';
import useFormValidator from 'components/app/utils/hooks/useFormValidator';
import Alert from 'components/app/common/alert';
import NotificationPopup from 'ComponentsV2/CommonComponents/NotificationPopup';
import PhoneInput from 'react-phone-input-2';
import LoginWithMicrosoft from './loginWithMicrosoft/loginWithMicrosoft';

import './login.scss';

import styles from './index.module.scss';
import 'react-phone-input-2/lib/style.css';
import useGoogleLogin from './loginWithGoogle/useGoogleLogin';
import {
    armIntegrationLogin,
    clientFromIntegration,
    completeIntegrationLogin,
    dropIdleIntegrationLogin,
    integrationArmed,
    isIntegrationQuery,
    readAuthorizeSearch,
    saveIntegrationLogin,
} from './integrationLogin';

const LAST_VALID_PAGE_KEY = 'last_valid_page';

function Login(props) {
    const logoSource = '/assets/gevme-new-logo.png';
    const gevmeMark = '/assets/GEVME_LOGO.svg';
    const destination = window.localStorage.getItem(LAST_VALID_PAGE_KEY) || '';
    const callbackUrl = props.location.state
        ? props.location.state.callbackUrl
        : null;

    const { history, location } = props;
    const onAuthorize = location.pathname === '/authorize';
    const integration = onAuthorize
        ? readAuthorizeSearch(location.search)
        : null;
    const connectClient = isIntegrationQuery(integration)
        ? clientFromIntegration(integration)
        : null;
    const [iconIndex, setIconIndex] = useState(0);

    useEffect(() => {
        setIconIndex(0);
    }, [connectClient?.name]);
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [loginByEmail, setLoginByEmail] = useState(true);
    const [sendBySms, setSendBySms] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const isSponsor = !!ls.get('sponsorAuth');
    const hasSponsorAuth = !!ls.get('sponsorTempAuth');

    const { alert, showAlert } = useAlert();
    const { handleLoginWithGoogle, googleError } = useGoogleLogin();

    const { validateInput, renderFieldError, hasErrors } = useFormValidator({
        input: {
            email,
            phoneNumber,
            type: loginByEmail ? 'email' : 'phoneNumber',
        },
        validationCriteria: loginValidationCriteria,
    });

    useEffect(() => {
        if (onAuthorize && isIntegrationQuery(integration)) {
            saveIntegrationLogin(integration);
        }
        if (location.search.includes('code=')) return;
        if (onAuthorize) return;
        dropIdleIntegrationLogin();
        if (integrationArmed() && ls.get('auth')) {
            completeIntegrationLogin().catch((error) => {
                showAlert(error.message, 'error');
            });
            return;
        }
        if (hasSponsorAuth) {
            history.replace('/company-profiles');
            return;
        }
        if (isSponsor) {
            history.replace('/login');
            return;
        }

        const auth = ls.get('auth');
        const require2FASetup = ls.get('require2FASetup');

        if (auth && require2FASetup) {
            history.replace({
                pathname: '/2fa-setup',
                payload: 'sso',
            });
            return;
        }

        if (callbackUrl) {
            ls('customProjectUrl', callbackUrl);

            const matchEmail = callbackUrl.match(/email=([^&]*)/);
            if (matchEmail && matchEmail.length && matchEmail[1]) {
                try {
                    let email = matchEmail[1];

                    if (email) {
                        email = decodeURIComponent(email);
                        setEmail(email);
                        login(
                            {
                                email,
                                sms: sendBySms,
                            },
                            false
                        );
                    } else if (auth) {
                        // Clean up (optional, to prevent stale redirects later)
                        window.localStorage.removeItem(LAST_VALID_PAGE_KEY);
                        history.replace(destination || callbackUrl);
                    }
                } catch (e) {
                    console.error(e);
                }
            }

            // on login we get the projectId
            // lets save it to localstorage coz if we still search this in history we get undefined
            // this is only applicable on switch account
            ls('tempProjId', callbackUrl?.split('/')[2]);
        } else if (auth) {
            // Clean up (optional, to prevent stale redirects later)
            window.localStorage.removeItem(LAST_VALID_PAGE_KEY);
            history.replace(destination || callbackUrl);
        }
    }, [history, onAuthorize]);

    useEffect(() => {
        if (googleError) {
            showAlert(googleError, 'error');
        }
    }, [googleError]);

    const login = async (data, validate = true) => {
        let _hasErrors = false;

        if (validate) {
            const formErrors = validateInput();

            _hasErrors = hasErrors(formErrors);
        }

        if (!_hasErrors) {
            setIsBusy(true);

            const response = await generateOtp(data.formData, data.type);
            if (loginByEmail)
                determineFlowUponResponse(response, data.formData?.email);
            else {
                determineFlowUponResponse(response, data.formData?.phoneNo);
            }
        }
    };
    /**
     * submits email to generate magic link
     * @param {object} e event object
     */
    const submitData = async (e) => {
        e.preventDefault();
        ls.get('gevme-callback-url') && ls.remove('gevme-callback-url');
        armIntegrationLogin();
        if (loginByEmail) {
            await login({
                formData: { email },
                type: 'email',
            });
        } else {
            await login({
                formData: { phoneNo: phoneNumber?.value },
                type: 'phone',
            });
        }
    };

    /**
     * determines the flow after receiving "submit" response
     * @param {object} response response from `generateOtp()`
     */
    const determineFlowUponResponse = async (response, value) => {
        const responseJson = await response.json();

        setIsBusy(false);

        if (responseJson.status) {
            history.push({
                pathname: '/otp',
                state: value,
                callbackUrl,
                identifier: responseJson?.identifier,
            });
        } else if (response.status === 403) {
            history.push('/no-access');
        } else {
            showAlert(responseJson.message, 'error');
        }
    };

    /**
     * handles changes to input
     * @param {object} e event object
     */
    const handleInputChange = (e) => {
        setEmail(e.target.value);
    };
    const handleInputChangePhone = (value, countryCode) => {
        setPhoneNumber({ value, countryCode });
    };
    return (
        <>
            <Alert alert={alert} />
            <div className={`${styles.login} login`}>
                <div className={`${styles.wrapper} tw-flex-col`}>
                    <NotificationPopup />
                    <div className={styles.innerWrapper}>
                        <div className="tw-py-14 tw-px-5">
                            <form
                                className="tw-p-0 tw-h-auto"
                                onSubmit={submitData}
                            >
                                {connectClient ? (
                                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-mb-8">
                                        <div
                                            className="tw-flex tw-items-center tw-gap-3"
                                            role="img"
                                            aria-label={`Gevme and ${connectClient.name}`}
                                        >
                                            <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-w-16 tw-h-16 tw-rounded-2xl tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-shadow-sm tw-p-2 tw-overflow-hidden">
                                                <img
                                                    src={gevmeMark}
                                                    alt=""
                                                    className="tw-w-11 tw-h-auto tw-object-contain"
                                                />
                                            </div>
                                            <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1.5 tw-text-gray-400">
                                                <span className="tw-w-1 tw-h-1 tw-rounded-full tw-bg-gray-300" />
                                                <span className="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-bg-gray-100 tw-text-gray-500">
                                                    <svg
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        aria-hidden="true"
                                                    >
                                                        <path d="M8 3 4 7l4 4" />
                                                        <path d="M4 7h16" />
                                                        <path d="m16 21 4-4-4-4" />
                                                        <path d="M20 17H4" />
                                                    </svg>
                                                </span>
                                                <span className="tw-w-1 tw-h-1 tw-rounded-full tw-bg-gray-300" />
                                            </div>
                                            <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-w-16 tw-h-16 tw-rounded-2xl tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-shadow-sm tw-p-2 tw-overflow-hidden">
                                                {connectClient.icons &&
                                                iconIndex <
                                                    connectClient.icons
                                                        .length ? (
                                                    <img
                                                        key={
                                                            connectClient.icons[
                                                                iconIndex
                                                            ]
                                                        }
                                                        src={
                                                            connectClient.icons[
                                                                iconIndex
                                                            ]
                                                        }
                                                        alt=""
                                                        onError={() =>
                                                            setIconIndex(
                                                                (prev) =>
                                                                    prev + 1
                                                            )
                                                        }
                                                        onLoad={(event) => {
                                                            const src =
                                                                connectClient
                                                                    .icons[
                                                                    iconIndex
                                                                ];
                                                            const width =
                                                                event.target
                                                                    .naturalWidth;
                                                            // Bundled SVGs have no pixel size. Remote
                                                            // icons below 64px only scale into a blur.
                                                            if (
                                                                /^https?:/.test(
                                                                    src
                                                                ) &&
                                                                width > 0 &&
                                                                width < 64
                                                            ) {
                                                                setIconIndex(
                                                                    (prev) =>
                                                                        prev + 1
                                                                );
                                                            }
                                                        }}
                                                        className="tw-w-10 tw-h-10 tw-object-contain"
                                                    />
                                                ) : (
                                                    <span
                                                        className="tw-flex tw-items-center tw-justify-center tw-w-full tw-h-full tw-bg-gray-900 tw-text-white tw-text-2xl tw-font-bold tw-font-ModernEra tw-rounded-xl"
                                                        aria-label={
                                                            connectClient.name
                                                        }
                                                    >
                                                        {connectClient.name
                                                            .slice(0, 1)
                                                            .toUpperCase()}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <h1 className="tw-text-var-gray-900 tw-text-center tw-font-ModernEra tw-text-[28px] tw-font-bold tw-leading-tight tw-m-0">
                                            Connecting Gevme to{' '}
                                            {connectClient.name}
                                        </h1>
                                        <p className="tw-mb-0 tw-text-center tw-text-gray-600">
                                            {connectClient.kind === 'local' &&
                                            connectClient.name === 'a local app'
                                                ? 'Log in to connect your Gevme account to an app running on this device.'
                                                : `Log in to connect your Gevme account to ${connectClient.name}.`}
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <img
                                            src={logoSource}
                                            alt="logo"
                                            className="login__logo tw-mb-2"
                                        />

                                        <span className="tw-text-var-gray-900 tw-text-center tw-font-ModernEra tw-text-[44px] tw-font-bold tw-leading-normal tw-tracking-[-1.76px]">
                                            Omnichannel
                                        </span>

                                        <p className="tw-mb-12">
                                            {onAuthorize
                                                ? 'This connection link is invalid.'
                                                : 'Log in to your Gevme Omnichannel account'}
                                        </p>
                                    </>
                                )}

                                <div className="tw-flex tw-justify-between tw-w-full mb-2">
                                    <span className="tw-text-gray-700 tw-font-open-sans tw-text-sm tw-font-semibold tw-leading-normal">
                                        {loginByEmail
                                            ? 'Email'
                                            : 'Phone Number'}
                                    </span>
                                    <span
                                        className="tw-text-blue-500 tw-font-open-sans tw-text-sm tw-font-semibold tw-leading-normal tw-text-right tw-cursor-pointer "
                                        onClick={() =>
                                            setLoginByEmail(!loginByEmail)
                                        }
                                    >
                                        {loginByEmail
                                            ? 'Log in with Phone Number'
                                            : 'Log in with Email'}
                                    </span>
                                </div>

                                {loginByEmail ? (
                                    <div className="form-group tw-mb-8">
                                        <input
                                            type="email"
                                            name="email"
                                            value={email}
                                            className="form-control tw-py-3.5 tw-px-4 tw-min-h-[48px]"
                                            onChange={handleInputChange}
                                            placeholder="Enter your email"
                                            autoComplete="off"
                                            required={loginByEmail}
                                        />
                                        {renderFieldError('email')}
                                    </div>
                                ) : (
                                    <>
                                        <div className="form-group tw-mb-8">
                                            <PhoneInput
                                                name="phoneNo"
                                                country="sg"
                                                enableSearch
                                                value={phoneNumber?.value}
                                                inputStyle={{
                                                    width: '100%',
                                                    minHeight: '48px',
                                                }}
                                                className="phoneInput-Login"
                                                onChange={(
                                                    value,
                                                    data,
                                                    _,
                                                    formattedValue
                                                ) => {
                                                    handleInputChangePhone(
                                                        formattedValue.replaceAll(
                                                            '-',
                                                            ''
                                                        ),
                                                        data.countryCode
                                                    );
                                                }}
                                            />

                                            {renderFieldError('phoneNumber')}
                                        </div>
                                        <style>{`
                                    .phoneInput-Login .country-list .country{
                                        display: flex;
                                        align-items: center;
                                    }
                                    .phoneInput-Login .country-list .search{
                                        display: flex;
                                        align-items: center;
                                    }
                                    `}</style>
                                    </>
                                )}

                                {/* <div className="checkbox-set">
                                    <label>
                                        <input
                                            type="checkbox"
                                            value={sendBySms}
                                            onChange={() => {
                                                setSendBySms(!sendBySms);
                                            }}
                                            id="sendBySms"
                                        />{' '}
                                        <span>Send OTP by SMS</span>
                                    </label>
                                </div> */}

                                <button
                                    type="submit"
                                    className="btn btn-primary btn-block tw-flex tw-gap-1 tw-items-center tw-justify-center"
                                    disabled={!!isBusy}
                                >
                                    {isBusy ? (
                                        <span className="tw-flex tw-gap-1 tw-items-center tw-justify-center">
                                            <img
                                                src="/assets/icon_spinner.gif"
                                                alt=""
                                            />
                                            Please wait...
                                        </span>
                                    ) : (
                                        'Next'
                                    )}
                                </button>
                            </form>

                            <div>
                                <div className="tw-w-full tw-h-fit tw-flex tw-flex-row tw-gap-4 tw-justify-center tw-items-center tw-my-4">
                                    <div className="tw-w-full tw-h-[1px] tw-bg-gray-300" />
                                    <span className="tw-w-fit tw-font-inter tw-font-normal tw-text-base tw-leading-base tw-text-gray-500">
                                        Or
                                    </span>
                                    <div className="tw-w-full tw-h-[1px] tw-bg-gray-300" />
                                </div>

                                <button
                                    className="google-btn tw-rounded-md tw-py-[10px] tw-w-full tw-flex tw-gap-2 tw-items-center tw-justify-center tw-hover:tw-bg-gray-100"
                                    onClick={handleLoginWithGoogle}
                                    disabled={!!isBusy}
                                >
                                    <img
                                        src="/assets/google-icon.svg"
                                        alt="google icon"
                                        className="tw-w-5 tw-h-5"
                                    />
                                    <span className="tw-font-semibold">
                                        Login with Google
                                    </span>
                                </button>

                                <a
                                    href={`${process.env.REACT_APP_SSO_REDIRECT_URI}`}
                                    className="font-note tw-no-underline tw-my-4 tw-text-sm tw-block"
                                    target="_self"
                                    onClick={() => armIntegrationLogin()}
                                >
                                    Log in with SSO
                                </a>
                            </div>

                            <div className="tw-mt-4">
                                <p className="foot-note tw-p-0">
                                    By logging in you agree to the{' '}
                                    <a
                                        href="https://www.gevme.com/en/terms-of-use/"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Terms of Use
                                    </a>{' '}
                                    and{' '}
                                    <a
                                        href="https://www.gevme.com/en/privacy-policy/"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Privacy Policy
                                    </a>
                                </p>
                            </div>
                        </div>

                        {callbackUrl && (
                            <LoginWithMicrosoft callbackUrl={callbackUrl} />
                        )}

                        <div className="tw-flex tw-w-full tw-py-4 tw-px-2.5 tw-justify-center tw-items-center tw-gap-2 tw-self-stretch tw-bg-[#E9ECF0] tw-rounded-[20px] tw-rounded-t-none">
                            <span className="tw-text-gray-700 tw-font-open-sans tw-text-sm tw-font-normal tw-leading-normal">
                                Do not have an account?
                            </span>
                            <a
                                href="https://www.gevme.com/en/request-a-demo/"
                                target="_blank"
                                rel="noreferrer"
                                className="tw-text-blue-500 tw-font-open-sans tw-text-sm tw-font-normal tw-leading-normal tw-text-right tw-cursor-pointer tw-no-underline hover:tw-underline"
                            >
                                Request a demo
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default withRouter(Login);
